import { useState, useRef } from "react";

import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import { SuggestionsPopper } from "./SendMessageBox";

import * as lodash from "lodash";

import { useProjectCaptions } from "./hooks";

type AutocompleteStoryInlineProps = {
  onSelected: (value: string) => void;
  children: any;
};

export function AutocompleteStoryInline({
  onSelected,
  children,
}: AutocompleteStoryInlineProps) {
  const [input, setInput] = useState<boolean>(false);
  const [value, setValue] = useState<string>("");
  const inputRef = useRef<HTMLInputElement>();
  const suggestionsRef = useRef<any[]>();
  const [selected, setSelected] = useState<number>(0);
  const captions = useProjectCaptions();

  const selectPrev = () => {
    if (suggestionsRef.current && suggestionsRef.current.length) {
      const index = Math.max(0, selected - 1);
      setSelected(index);
    }
  };

  const selectNext = () => {
    if (suggestionsRef.current && suggestionsRef.current.length > 0) {
      const index = Math.min(suggestionsRef.current.length - 1, selected + 1);
      setSelected(index);
    }
  };

  const changeInput = (state: boolean) => {
    setInput(state);
  };

  const handleKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      onSelected((e.target as any).value);
      changeInput(false);
      setValue("");
    } else if (e.key === "Escape") {
      changeInput(false);
      setValue("");
    } else if (e.key === "Tab") {
      e.preventDefault();

      let suggestion: string = (e.target as any).value;
      if (suggestionsRef.current && suggestionsRef.current.length) {
        const index = lodash.clamp(
          selected,
          0,
          suggestionsRef.current.length - 1
        );
        suggestion = suggestionsRef.current[index].text;
      }

      onSelected(suggestion);
      changeInput(false);
      setValue("");
    } else if (e.key === "ArrowUp" || (e.ctrlKey && e.key === "k")) {
      selectPrev();
    } else if (e.key === "ArrowDown" || (e.ctrlKey && e.key === "j")) {
      selectNext();
    }
  };

  const handleTextInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
    setSelected(0);
  };

  const handleSuggestionSelected = (value: string) => {
    setValue(value);
    onSelected(value);
    changeInput(false);
    setValue("");
  };

  const handleBlur = () => {
    setTimeout(() => {
      changeInput(false);
      setValue("");
    }, 300);
  };

  return (
    <>
      {!input && (
        <Box
          onClick={() => {
            changeInput(true);
          }}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            cursor: 'pointer',
          }}
        >
          {children}
        </Box>
      )}
      {input && (
        <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
          <TextField
            sx={{
              width: '180px',
              '& .MuiInput-root': {
                fontSize: '10px',
                height: '18px',
                '&:before': {
                  borderBottom: (theme) =>
                    `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'}`
                },
                '&:after': {
                  borderBottomColor: 'var(--accent-primary)',
                  borderBottomWidth: '1px',
                },
                '&:hover:not(.Mui-disabled):before': {
                  borderBottom: (theme) =>
                    `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'}`
                },
              },
              '& .MuiInput-input': {
                fontSize: '10px',
                height: '18px',
                padding: '0 4px',
                color: 'var(--text-secondary)',
              },
            }}
            size="small"
            variant="standard"
            autoFocus
            placeholder="Link story..."
            onChange={handleTextInput}
            value={value}
            onBlur={handleBlur}
            onKeyDown={handleKey}
            inputRef={inputRef}
          />
          {value && inputRef && inputRef.current && (
            <SuggestionsPopper
              anchorRef={inputRef}
              buffer={{ text: value }}
              suggestionsRef={suggestionsRef}
              onSelected={handleSuggestionSelected}
              selectedIndex={selected}
              options={captions}
            />
          )}
        </Box>
      )}
    </>
  );
}
